import { useLayoutEffect, useState } from "react";

// A takeover is a full-screen message that REPLACES the app: the trial has
// expired, the account is blocked, two-factor is now required. They are all
// FullScreenMessage, so registering here means anything that takes the app
// over is counted - including whatever gets written next - rather than each
// caller re-deriving the same account conditions and drifting apart.
//
// Anything optional is expected to ask before putting itself on screen. A
// dialog at z-index 1400 will happily cover a takeover at 100, and "Encharge
// Dark Mode - what should we build next?" over "your trial has ended" is not a
// thing to say to somebody.

let showing = 0;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export const isAppTakenOver = (): boolean => showing > 0;

/** Called by FullScreenMessage while it is on screen. Returns the release. */
export const registerTakeover = (): (() => void) => {
  showing += 1;
  notify();
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    showing -= 1;
    notify();
  };
};

/** Re-renders when the app is taken over, or stops being. */
export const useAppTakenOver = (): boolean => {
  const [takenOver, setTakenOver] = useState(isAppTakenOver);
  // A layout effect, so a takeover arriving over an already-open dialog hides
  // it in the same commit. React 17 flushes passive effects AFTER paint, which
  // would leave one frame of the announcement sitting on top of the lock
  // screen - the same reason topBannerSlot resolves ownership the way it does.
  useLayoutEffect(() => {
    const onChange = () => setTakenOver(isAppTakenOver());
    listeners.add(onChange);
    onChange();
    return () => {
      listeners.delete(onChange);
    };
  }, []);
  return takenOver;
};
